import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, MenuItem, FormControl, Select, useTheme } from '@mui/material';
import DriveItem from './DriveItem';
import { getDrives } from '../../apis/agent';
import { useLocation } from 'react-router-dom';

export default function DriveList() {
  const location = useLocation();
  const { device } = location.state || {};
  const uuid = device?.uuid || '';
  const sys_uuid = device?.sys_uuid || '';
  const device_id = device?.id || '';
  const licence_id = device?.licence_id || '';
  const allowedDisks = device?.disks_info || [];
  const licenceType = device?.licenceType || '';
  const deletions = device?.deletion||0;
  const enc_dec= device?.enc_dec||0

  const theme = useTheme();

  const [sortOption, setSortOption] = useState('Sort by Size');
  const [drives, setDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sortOptions = ['Sort by Size', 'Sort by Status High', 'Sort by Status Low'];

  // Fetch drives
  useEffect(() => {
    const fetchDrives = async () => {
      if (!uuid) {
        setError('Device UUID is missing.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getDrives(uuid, sys_uuid);

        if (response && Array.isArray(response.data)) {
          const mappedDrives = response.data.map((drive) => {
            const totalGB = Math.round(drive.total_space / (1024 * 1024 * 1024));
            const freeGB = Math.round(drive.free_space / (1024 * 1024 * 1024));
            const usedGB = totalGB - freeGB;
            const percentage = Math.round((usedGB / totalGB) * 100);

            let status;
            if (percentage < 50) status = 'Healthy';
            else if (percentage < 80) status = 'Warning';
            else status = 'Critical';

            const driveLetter = `${drive.drive_letter}/`;
            let isAllowed = true;
            //console.log(licenceType, allowedDisks);
            if (licenceType !== 'single_device') {
              isAllowed = allowedDisks.includes(drive.serial_number);
              //console.log(isAllowed, drive.serial_number);
            } else {
              isAllowed = true;
            }

            return {
              name: driveLetter,
              label: `${totalGB}GB ${drive.drive_type}`,
              used: usedGB,
              total: totalGB,
              status,
              uuid,
              isAllowed,
            };
          });
          setDrives(mappedDrives);
        } else {
          console.error('Invalid response format:', response);
          setDrives([]);
          setError('Failed to load drives: Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching drives:', err);
        setDrives([]);
        if (err.message === 'UUID is required to fetch drives') {
          setError('Failed to load drives: Device UUID is required.');
        } else if (err.response) {
          setError(`Failed to load drives: Server error (${err.response.status})`);
        } else if (err.request) {
          setError(
            'Failed to load drives: Unable to connect to the server. This may be due to a CORS policy issue or the agent being offline.'
          );
        } else {
          setError(`Failed to load drives: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDrives();
  }, [uuid]);

  // Sorting logic
  const getSortedDrives = () => {
    const statusOrder = { Healthy: 1, Warning: 2, Critical: 3 };
    return [...drives].sort((a, b) => {
      if (sortOption === 'Sort by Size') return a.total - b.total;
      else if (sortOption === 'Sort by Status Low') return statusOrder[a.status] - statusOrder[b.status];
      else if (sortOption === 'Sort by Status High') return statusOrder[b.status] - statusOrder[a.status];
      return 0;
    });
  };

  const sortedDrives = getSortedDrives();

  // Loading state
  if (loading) {
    return (
      <Box
        bgcolor={theme.palette.background.paper}
        borderRadius={2}
        boxShadow={2}
        p={{ xs: 2, md: 3 }}
        width="100%"
      >
        <Typography variant="h5" fontWeight={700} mb={2}>
          Available Drives
        </Typography>
        <Box textAlign="center" fontSize={16} color={theme.palette.text.secondary}>
          <CircularProgress color="primary" size={24} />
          <Typography mt={1}>Loading drives...</Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        bgcolor={theme.palette.background.paper}
        borderRadius={2}
        boxShadow={2}
        p={{ xs: 2, md: 3 }}
        width="100%"
      >
        <Typography variant="h5" fontWeight={700} mb={2}>
          Available Drives
        </Typography>
        <Typography fontSize={16} color={theme.palette.error.main} textAlign="center">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      bgcolor={theme.palette.background.paper}
      borderRadius={2}
      boxShadow={2}
      p={{ xs: 2, md: 3 }}
      width="100%"
    >
      {/* Header & Sort */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        mb={2}
      >
        <Typography variant="h5" fontWeight={700}>
          Available Drives
        </Typography>

        <FormControl sx={{ minWidth: 160 }}>
          <Select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            size="small"
          >
            {sortOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Drives List */}
      {drives.length === 0 ? (
        <Typography fontSize={16} color={theme.palette.text.secondary} textAlign="center">
          No drives available.
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {sortedDrives.map((drive, index) => (
            <DriveItem
              key={index}
              {...drive}
              sys_uuid={sys_uuid}
              device_id={device_id}
              licence_id={licence_id}
              deletion={deletions}
              enc_dec={enc_dec}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
